import { useEffect, useRef } from "react";

const MAX_STARS = 260;
const CURSOR_LINK = 170;
const PAIR_LINK = 95;
const PAIR_ZONE = 220;
const COLOR = "245, 245, 240";

const CONSTELLATIONS = [
  {
    name: "Orion", at: [0.62, 0.36],
    stars: [[0.48, 0], [0.2, 0.15], [0.75, 0.2], [0.38, 0.52], [0.5, 0.5], [0.62, 0.48], [0.3, 0.9], [0.8, 0.85]],
    edges: [[0, 1], [0, 2], [1, 3], [2, 5], [3, 4], [4, 5], [3, 6], [5, 7]],
  },
  {
    name: "Saptarishi · Big Dipper", at: [0.85, 0.16],
    stars: [[0, 0.2], [0.18, 0.25], [0.33, 0.35], [0.48, 0.45], [0.52, 0.75], [0.85, 0.8], [0.9, 0.5]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 3]],
  },
  {
    name: "Cassiopeia", at: [0.36, 0.1],
    stars: [[0, 0.3], [0.25, 0.8], [0.5, 0.45], [0.75, 0.9], [1, 0.2]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 4]],
  },
  {
    name: "Cygnus", at: [0.58, 0.8],
    stars: [[0.5, 0], [0.5, 0.35], [0.5, 1], [0.05, 0.3], [0.95, 0.45]],
    edges: [[0, 1], [1, 2], [3, 1], [1, 4]],
  },
  {
    name: "Scorpius · Vrischika", at: [0.14, 0.62],
    stars: [[0.05, 0], [0, 0.2], [0.12, 0.33], [0.3, 0.4], [0.45, 0.55], [0.5, 0.75], [0.6, 0.9], [0.8, 0.95], [0.95, 0.8], [0.9, 0.65]],
    edges: [[0, 2], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9]],
  },
];

const layoutConstellations = (w, h) => {
  const size = Math.max(90, Math.min(200, Math.min(w, h) * 0.24));
  return CONSTELLATIONS.map((c) => {
    const cx = c.at[0] * w;
    const cy = c.at[1] * h;
    const pts = c.stars.map(([x, y]) => ({ x: cx + (x - 0.5) * size, y: cy + (y - 0.5) * size }));
    return { ...c, cx, cy, size, pts, glow: 0 };
  });
};

const makeStar = (w, h, x, y) => ({
  x: x ?? Math.random() * w,
  y: y ?? Math.random() * h,
  r: 0.5 + Math.random() * 1.5,
  vx: (Math.random() - 0.5) * 0.18,
  vy: (Math.random() - 0.5) * 0.18,
});

export const ConstellationSky = ({ containerRef }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = containerRef.current;
    if (!canvas || !host) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, stars = [], groups = [], frame = 0, shooting = null, nextShoot = performance.now() + 4000;
    const pointer = { x: 0, y: 0, active: false };

    const resize = () => {
      const rect = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width; h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(130, Math.round((w * h) / 9000));
      stars = Array.from({ length: count }, () => makeStar(w, h));
      groups = layoutConstellations(w, h);
    };

    const local = (cx, cy) => {
      const rect = host.getBoundingClientRect();
      return { x: cx - rect.left, y: cy - rect.top };
    };
    const onMove = (e) => {
      const p = e.touches ? e.touches[0] : e;
      if (!p) return;
      const { x, y } = local(p.clientX, p.clientY);
      pointer.x = x; pointer.y = y; pointer.active = true;
    };
    const onLeave = () => { pointer.active = false; };
    const onClick = (e) => {
      const p = e.changedTouches ? e.changedTouches[0] : e;
      const { x, y } = local(p.clientX, p.clientY);
      for (let i = 0; i < 3; i += 1) stars.push(makeStar(w, h, x + (Math.random() - 0.5) * 40, y + (Math.random() - 0.5) * 40));
      if (stars.length > MAX_STARS) stars.splice(0, stars.length - MAX_STARS);
    };

    const line = (x1, y1, x2, y2, alpha) => {
      ctx.strokeStyle = `rgba(${COLOR}, ${alpha})`;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    };

    const drawConstellations = () => {
      for (const g of groups) {
        const near = pointer.active && Math.hypot(pointer.x - g.cx, pointer.y - g.cy) < g.size * 0.85;
        g.glow += ((near ? 1 : 0) - g.glow) * 0.06;
        if (g.glow > 0.01) {
          for (const [a, b] of g.edges) line(g.pts[a].x, g.pts[a].y, g.pts[b].x, g.pts[b].y, 0.45 * g.glow);
          ctx.font = "500 10px 'IBM Plex Mono', monospace";
          ctx.textAlign = "center";
          ctx.fillStyle = `rgba(${COLOR}, ${0.32 * g.glow})`;
          ctx.fillText(g.name.toUpperCase(), g.cx, g.cy + g.size * 0.5 + 18);
        }
        ctx.fillStyle = `rgba(${COLOR}, ${0.75 + 0.2 * g.glow})`;
        for (const p of g.pts) { ctx.beginPath(); ctx.arc(p.x, p.y, 1.4 + 0.6 * g.glow, 0, Math.PI * 2); ctx.fill(); }
      }
    };

    const drawShooting = (now) => {
      if (reduced) return;
      if (!shooting && now > nextShoot) {
        shooting = { x: Math.random() * w * 0.7, y: Math.random() * h * 0.4, vx: 7 + Math.random() * 4, vy: 2.5 + Math.random() * 2, life: 1 };
      }
      if (!shooting) return;
      const s = shooting;
      const grad = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * 12, s.y - s.vy * 12);
      grad.addColorStop(0, `rgba(${COLOR}, ${0.85 * s.life})`);
      grad.addColorStop(1, `rgba(${COLOR}, 0)`);
      ctx.strokeStyle = grad; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(s.x - s.vx * 12, s.y - s.vy * 12); ctx.stroke();
      ctx.lineWidth = 1;
      s.x += s.vx; s.y += s.vy; s.life -= 0.012;
      if (s.life <= 0 || s.x > w + 100 || s.y > h + 100) { shooting = null; nextShoot = now + 6000 + Math.random() * 8000; }
    };

    const tick = (now) => {
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 1;
      for (const s of stars) {
        if (!reduced) {
          s.x += s.vx; s.y += s.vy;
          if (s.x < 0 || s.x > w) { s.vx *= -1; s.x = Math.max(0, Math.min(w, s.x)); }
          if (s.y < 0 || s.y > h) { s.vy *= -1; s.y = Math.max(0, Math.min(h, s.y)); }
        }
      }
      if (pointer.active) {
        const near = stars.filter((s) => Math.hypot(s.x - pointer.x, s.y - pointer.y) < PAIR_ZONE);
        for (const s of near) {
          const d = Math.hypot(s.x - pointer.x, s.y - pointer.y);
          if (d < CURSOR_LINK) line(s.x, s.y, pointer.x, pointer.y, 0.55 * (1 - d / CURSOR_LINK));
        }
        for (let i = 0; i < near.length; i += 1) {
          for (let j = i + 1; j < near.length; j += 1) {
            const d = Math.hypot(near[i].x - near[j].x, near[i].y - near[j].y);
            if (d < PAIR_LINK) line(near[i].x, near[i].y, near[j].x, near[j].y, 0.45 * (1 - d / PAIR_LINK));
          }
        }
      }
      ctx.fillStyle = `rgba(${COLOR}, 0.85)`;
      for (const s of stars) { ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill(); }
      drawConstellations();
      drawShooting(now);
      frame = requestAnimationFrame(tick);
    };

    resize();
    frame = requestAnimationFrame(tick);
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    host.addEventListener("mousemove", onMove);
    host.addEventListener("mouseleave", onLeave);
    host.addEventListener("touchmove", onMove, { passive: true });
    host.addEventListener("touchend", onLeave);
    host.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      host.removeEventListener("mousemove", onMove);
      host.removeEventListener("mouseleave", onLeave);
      host.removeEventListener("touchmove", onMove);
      host.removeEventListener("touchend", onLeave);
      host.removeEventListener("click", onClick);
    };
  }, [containerRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="constellation-canvas"
      className="pointer-events-none"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 5 }}
    />
  );
};
